/**
 * Shared drawing / interaction helpers for the two large "dyna dimmer"
 * graph components (SampledynagraphComponent and
 * SamplegraphdynanontimeComponent). Both hosts share almost identical
 * `drawLineAndPath`, `addDrag`, and `dragended` implementations that
 * differ only in a few cosmetic parameters (gradient id, x-label anchor,
 * output clamping, x-grid tick format).
 *
 * The helpers below are `this`-typed free functions so each host can
 * delegate via `helper.call(this, ...)` without inheritance or Angular
 * `@Input()` propagation concerns.
 */

import * as d3 from "d3";

/** Context required by the draw helper (i.e. what `this` must expose). */
export interface DynaGraphDrawContext {
  x: LegacyAny;
  y: LegacyAny;
  width: number;
  height: number;
  margin: { top: number; right: number; bottom: number; left: number };
  data: LegacyAny[];
  color2: string;
  readOnly: boolean;
  line: LegacyAny;
  area: LegacyAny;
  focus: LegacyAny;
  make_x_gridlines(): LegacyAny;
  make_y_gridlines(): LegacyAny;
}

/** Per-host parameters for `drawDynaLineAndPath`. */
export interface DynaGraphDrawOptions {
  /** The `d3.select(...)`-ed <svg> element the host owns (svg3 or svg4). */
  hostSvg: LegacyAny;
  /** Unique gradient id used for fill (e.g. "gradient5", "gradient4"). */
  gradientId: string;
  /** X coordinate (in data units) where the x-axis label is anchored. */
  xLabelAnchor: number;
  /** When true, dyanYOutput is clamped to [0, 100] before projecting to Y. */
  clampY: boolean;
  /** Optional inline tickFormat applied to the x-grid axis. */
  xGridExtraFormat?: (d: LegacyAny, i: number) => LegacyAny;
  /**
   * DOM selector for the <svg> element (used only when clearing existing
   * children). Defaults to "#svg3" for backwards compat with the two
   * original dyna hosts.
   */
  svgSelector?: string;
  /** X-axis label. Defaults to "Time (hh:mm)". */
  xAxisLabel?: string;
  /** When true (default), render x-axis label via .html(); otherwise .text(). */
  xAxisLabelIsHtml?: boolean;
  /** Y-axis label. Defaults to "Output level (%)". */
  yAxisLabel?: string;
  /** Y coordinate offset for the x-axis label. Defaults to -20. */
  xLabelYOffset?: number;
  /** Circle radius for the data points. Defaults to 6.0. */
  circleRadius?: number;
  /** X coordinate override for the rotated y-axis label. Defaults to -height/2. */
  yLabelX?: number;
}

/**
 * Draws the dyna-dimmer line, area, points, gradient, axes, labels, and
 * gridlines into `opts.hostSvg`. Mirrors the previous copy-pasted
 * `drawLineAndPath` implementations exactly.
 */
export function drawDynaLineAndPath(
  this: DynaGraphDrawContext,
  opts: DynaGraphDrawOptions
): void {
  const clampFn = opts.clampY
    ? (val: number) => Math.min(100, Math.max(0, val))
    : (val: number) => val;
  const svgSelector = opts.svgSelector ?? "#svg3";
  const xAxisLabel = opts.xAxisLabel ?? "Time (hh:mm)";
  const xAxisLabelIsHtml = opts.xAxisLabelIsHtml ?? true;
  const yAxisLabel = opts.yAxisLabel ?? "Output level (%)";
  const xLabelYOffset = opts.xLabelYOffset ?? -20;
  const circleRadius = opts.circleRadius ?? 6.0;

  this.line = d3
    .line()
    .x((d: LegacyAny) => this.x(d.dyanXTime))
    .y((d: LegacyAny) => this.y(clampFn(d.dyanYOutput)));

  d3.selectAll(svgSelector + " > *").remove();

  this.area = d3
    .area()
    .x((d: LegacyAny) => this.x(d.dyanXTime))
    .y0(this.height)
    .y1((d: LegacyAny) => this.y(clampFn(d.dyanYOutput)));

  this.focus = opts.hostSvg
    .append("g")
    .attr(
      "transform",
      `translate(${this.margin.left},${this.margin.top})`
    );

  // plotting Area
  this.focus
    .append("path")
    .datum(this.data)
    .attr("fill", "#1ECBD0")
    .attr("fill-opacity", 0.3)
    .attr("stroke", "#1ECBD0")
    .attr("stroke-width", "1px")
    .attr("fill", `url('#${opts.gradientId}')`)
    .attr("d", this.area);

  // plotting circles
  this.focus
    .selectAll("circle")
    .data(this.data)
    .enter()
    .append("circle")
    .attr("r", circleRadius)
    .attr("cx", (d: LegacyAny) => this.x(d.dyanXTime))
    .attr("cy", (d: LegacyAny) => this.y(clampFn(d.dyanYOutput)))
    .style("cursor", this.readOnly ? "default" : "pointer")
    .style("fill", "#1ECBD0");

  const maxY = d3.max(this.data, function (d) {
    return d.dyanYOutput;
  });

  // Gradient
  opts.hostSvg
    .append("linearGradient")
    .attr("id", opts.gradientId)
    .attr("gradientUnits", "userSpaceOnUse")
    .attr("x1", 0)
    .attr("y1", this.y(0))
    .attr("x2", 0)
    .attr("y2", this.y(maxY))
    .selectAll("stop")
    .data([
      { offset: "10%", color: "transparent" },
      { offset: "30%", color: "#00E487" },
      { offset: "100%", color: this.color2 },
    ])
    .enter()
    .append("stop")
    .attr("offset", function (d) {
      return d.offset;
    })
    .attr("stop-color", function (d) {
      return d.color;
    });

  // x axis frame (line only; ticks come from the grid below)
  this.focus
    .append("g")
    .attr("class", "axis axis--x")
    .style("color", "white")
    .attr("transform", `translate(0,${this.height})`);

  // x-axis label
  const xLabelSelection = opts.hostSvg
    .append("text")
    .attr("x", this.x(opts.xLabelAnchor))
    .attr("y", this.y(xLabelYOffset))
    .style("text-anchor", "middle")
    .style("font", "16px Signify-Relative-Bold");
  if (xAxisLabelIsHtml) {
    xLabelSelection.html(xAxisLabel);
  } else {
    xLabelSelection.text(xAxisLabel);
  }

  // y axis
  this.focus
    .append("g")
    .attr("class", "axis axis--y")
    .call(d3.axisLeft(this.y));

  // y-axis label
  opts.hostSvg
    .append("text")
    .attr("transform", "rotate(-90)")
    .attr("y", 0 - this.margin.left + 62)
    .attr("x", opts.yLabelX ?? (0 - this.height / 2))
    .style("text-anchor", "middle")
    .style("font", "16px Signify-Relative-Bold")
    .text(yAxisLabel);

  // X gridlines
  let xGrid = this.make_x_gridlines().tickSize(-this.height);
  if (opts.xGridExtraFormat) {
    xGrid = xGrid.tickFormat(opts.xGridExtraFormat);
  }
  this.focus
    .append("g")
    .attr("transform", `translate(0,${this.height})`)
    .call(xGrid);

  // Y gridlines
  this.focus
    .append("g")
    .call(this.make_y_gridlines().tickSize(-this.width).tickFormat(""));
}

/** Context required by the drag helpers. */
export interface DynaGraphDragContext {
  readOnly: boolean;
  focus: LegacyAny;
  x: LegacyAny;
  y: LegacyAny;
  area: LegacyAny;
  changedPoint: LegacyAny;
  dragstarted(): DynaGraphDragContext;
  dragended(d?: LegacyAny): void;
}

/**
 * Wires up d3's drag behaviour on the circles rendered by
 * `drawDynaLineAndPath`. Read-only mode is a no-op. On drag, the
 * changed point index is captured, the data mutated to reflect the
 * pointer position (clamped to 0..24 hours / 0..100 %), and the area
 * path re-rendered.
 */
export function addDynaDrag(this: DynaGraphDragContext): void {
  if (this.readOnly) {
    return;
  }
  const dragstartedRef = this.dragstarted.bind(this);
  const dragendedRef = this.dragended.bind(this);
  const selection = this.focus.selectAll("circle").call(
    d3
      .drag()
      .on("start", function (event, d) {
        dragstartedRef();
      })
      .on("drag", function (event, d) {
        const control = dragstartedRef();
        const n = selection.nodes();
        const i = n.indexOf(this);
        if (i >= 1 && i < n.length - 1) {
          control.changedPoint = i;
          const m = d3.select(n[i]).node();
          d.dyanXTime = control.x.invert(event.x);
          d.dyanYOutput = control.y.invert(event.y);
          if (d.dyanXTime > 24) {
            d.dyanXTime = 23.59;
          }
          if (d.dyanXTime < 0) {
            d.dyanXTime = 0.1;
          }
          if (d.dyanYOutput > 100) {
            d.dyanYOutput = 100;
          }
          if (d.dyanYOutput < 0) {
            d.dyanYOutput = 0;
          }
          d3.select(m)
            .attr("cx", control.x(d.dyanXTime))
            .attr("cy", control.y(d.dyanYOutput));
          control.focus.select("path").attr("d", control.area);
        }
      })
      .on("end", dragendedRef)
  );
}

/** Context required by `dragEndedDyna`. */
export interface DynaGraphDragEndContext {
  data: LegacyAny[];
  changedPoint: LegacyAny;
  getDynaTimeXRefernce(dynaModX: number): string;
  updateScenes2(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes3(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes4(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes5(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes6(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes7(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes8(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes9(data: LegacyAny[], draggedData: LegacyAny): void;
  updateScenes10(data: LegacyAny[], draggedData: LegacyAny): void;
  graphState: { sendDynaGraphData(payload: LegacyAny): void };
}

/**
 * Handles the end-of-drag lifecycle: convert the (mutated) data into
 * a `draggedData` payload keyed by scene size (2..10 config points) and
 * push it to the graph state service.
 */
export function dragEndedDyna(this: DynaGraphDragEndContext, _d?: LegacyAny): void {
  const draggedData: LegacyAny = [];

  if (this.data.length === 3 && this.changedPoint === 1) {
    const dynaModX = parseFloat(this.data[1].dyanXTime);
    const modTimeXValue = this.getDynaTimeXRefernce(dynaModX);
    draggedData.push({
      dyanXTime: modTimeXValue,
      dyanYOutput: parseInt(this.data[1].dyanYOutput),
      dynaZfadeTime: parseInt(this.data[1].dynaZfadeTime),
    });
  }

  if (this.data.length === 6) {
    this.updateScenes2(this.data, draggedData);
  }
  if (this.data.length === 8) {
    this.updateScenes3(this.data, draggedData);
  }
  if (this.data.length === 10) {
    this.updateScenes4(this.data, draggedData);
  }
  if (this.data.length === 12) {
    this.updateScenes5(this.data, draggedData);
  }
  if (this.data.length === 14) {
    this.updateScenes6(this.data, draggedData);
  }
  if (this.data.length === 16) {
    this.updateScenes7(this.data, draggedData);
  }
  if (this.data.length === 18) {
    this.updateScenes8(this.data, draggedData);
  }
  if (this.data.length === 20) {
    this.updateScenes9(this.data, draggedData);
  }
  if (this.data.length === 22) {
    this.updateScenes10(this.data, draggedData);
  }
  this.graphState.sendDynaGraphData(draggedData);
}

/**
 * Converts a parsed DynaDimmer scene-entry list into the flat graph
 * data-point array consumed by both dyna-graph components.
 *
 * Each entry is expected to have `timeHHMM` (e.g. "13:30"),
 * `outputLevel`, and `fadeTime` fields.
 *
 * The optional `timeTransform` converts each raw numeric time before it
 * is stored — the "non-time-referenced" graph variant uses this to apply
 * the 12-hour circular shift.
 */
export function buildDynaDataPoints(
  newData1: LegacyAny[],
  timeTransform: (rawTime: number) => number = (t) => t
): LegacyAny[] {
  const N = newData1.length;
  if (N === 0) {
    return [];
  }

  const entries = newData1.map((el) => ({
    time: timeTransform(parseFloat(el.timeHHMM.replace(":", "."))),
    output: parseInt(el.outputLevel),
    fadetime: parseInt(el.fadeTime),
  }));

  const result: LegacyAny[] = [];

  if (N === 1) {
    const { time, output, fadetime } = entries[0];
    result.push({ dyanXTime: 0, dyanYOutput: output, dynaZfadeTime: -1 });
    result.push({ dyanXTime: time, dyanYOutput: output, dynaZfadeTime: fadetime });
    result.push({ dyanXTime: 24, dyanYOutput: output, dynaZfadeTime: -1 });
    return result;
  }

  const lastOutput = entries[N - 1].output;
  result.push({ dyanXTime: 0, dyanYOutput: lastOutput, dynaZfadeTime: -1 });
  for (let i = 0; i < N; i++) {
    const { time, output, fadetime } = entries[i];
    const prevOutput = i === 0 ? lastOutput : entries[i - 1].output;
    result.push({ dyanXTime: time, dyanYOutput: prevOutput, dynaZfadeTime: fadetime });
    result.push({ dyanXTime: time, dyanYOutput: output, dynaZfadeTime: fadetime });
  }
  result.push({ dyanXTime: 24, dyanYOutput: lastOutput, dynaZfadeTime: -1 });
  return result;
}
